
import { motion, AnimatePresence } from "framer-motion";
import { X, Facebook, Instagram } from "lucide-react";
import { Button } from "./ui/button";

interface MainMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MainMenu = ({ isOpen, onClose }: MainMenuProps) => {
  const menuSections = [
    {
      title: "Sobre Nós",
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-[#004080] font-bold mb-2">Quem Somos</h3>
            <p className="text-gray-700">
              A Solucionar Soldas é uma empresa local especializada em serviços de soldagem, 
              mandrilhamento, estruturas metálicas e torneamento. Com anos de experiência no 
              mercado, atendemos clientes de diversos setores, oferecendo soluções personalizadas 
              e de alta qualidade.
            </p>
          </div>
          
          <div>
            <h3 className="text-[#004080] font-bold mb-2">Nossos Diferenciais</h3>
            <ul className="space-y-2 text-gray-700">
              <li>✔ Atendimento Personalizado: Trabalhamos lado a lado com nossos clientes</li>
              <li>✔ Qualidade Garantida: Materiais de alta resistência e equipamentos modernos</li>
              <li>✔ Experiência Técnica: Equipe com soldadores experientes e capacitados</li>
              <li>✔ Compromisso com o Prazo: Entregas dentro do prazo combinado</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: "Serviços",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <h3 className="text-[#004080] font-bold">Soldagem em Geral</h3>
            <p className="text-gray-700">
              Realizamos serviços de soldagem MIG, TIG e eletrodo revestido para diferentes 
              aplicações, garantindo resistência e precisão.
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="text-[#004080] font-bold">Mandrilhamento</h3>
            <p className="text-gray-700">
              Nosso serviço de mandrilhamento é ideal para ajustes finos e acabamento 
              preciso em peças metálicas.
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="text-[#004080] font-bold">Estruturas Metálicas</h3>
            <p className="text-gray-700">
              Fabricação e montagem de estruturas metálicas para diversos setores, incluindo 
              portões, mezaninos, coberturas e suportes industriais.
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="text-[#004080] font-bold">Torneamento Mecânico</h3>
            <p className="text-gray-700">
              Peças usinadas com precisão para reposição ou fabricação personalizada, 
              garantindo encaixe e durabilidade.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Contato",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-[#004080] font-bold mb-2">Endereço</h3>
                <p className="text-gray-700">
                  Rua Vicente Capoeira, 1153 - Pres. Olegário, MG, Brazil, 38750000
                </p>
              </div>
              <div>
                <h3 className="text-[#004080] font-bold mb-2">Horário de Atendimento</h3>
                <p className="text-gray-700">Segunda a Sexta, 8h às 17h</p>
              </div>
              <div className="flex flex-col gap-3">
                <Button 
                  className="bg-[#0073B7] hover:bg-[#004080] text-white"
                  onClick={() => window.open("https://wa.me/5534992505512", "_blank")}
                >
                  WhatsApp: (34) 99250-5512
                </Button>
                <Button 
                  className="bg-[#0073B7] hover:bg-[#004080] text-white"
                  onClick={() => window.open("https://wa.me/5534999894830", "_blank")}
                >
                  WhatsApp: (34) 99989-4830
                </Button>
                <div className="flex gap-4">
                  <Button
                    className="flex-1 bg-[#1877F2] hover:bg-[#0E5FC1] text-white"
                    onClick={() => window.open("https://www.facebook.com/solucionarsoldas/", "_blank")}
                  >
                    <Facebook className="mr-2 h-4 w-4" />
                    Facebook
                  </Button>
                  <Button
                    className="flex-1 bg-[#E4405F] hover:bg-[#D32D4B] text-white"
                    onClick={() => window.open("https://www.instagram.com/solucionarsoldas2019/", "_blank")}
                  >
                    <Instagram className="mr-2 h-4 w-4" />
                    Instagram
                  </Button>
                </div>
              </div>
            </div>
            <div className="w-full h-[300px] rounded-lg overflow-hidden">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3785.629185126357!2d-46.43616678857237!3d-18.40972028259013!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94ae9183f3ef07a9%3A0x9af447133a6054b6!2sR.%20Vicente%20Capoeira%2C%201153%20-%20Pres.%20Oleg%C3%A1rio%2C%20MG%2C%2038750-000!5e0!3m2!1spt-BR!2sbr!4v1738595517998!5m2!1spt-BR!2sbr"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="fixed inset-0 flex items-center justify-center p-4 z-40"
        >
          <div 
            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
            onClick={onClose}
          />
          <div className="relative w-full max-w-[800px] max-h-[90vh] bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 z-50"
              onClick={onClose}
            >
              <X className="h-6 w-6 text-[#004080]" />
            </Button>
            
            <div className="h-full max-h-[90vh] overflow-y-auto custom-scrollbar p-6">
              <div className="space-y-8 mt-4">
                {menuSections.map((section, index) => (
                  <div key={index} className="space-y-4">
                    <h2 className="text-2xl font-bold text-[#004080] border-b-2 border-[#0073B7] pb-2 sticky top-0 bg-white/95 backdrop-blur-md z-10">
                      {section.title}
                    </h2>
                    {section.content}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
